import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Disc, Users, ListMusic, Radio, Play } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Cover } from "@/components/Cover";
import { usePlayer } from "@/context/PlayerContext";
import { api, setLibMode, getLibMode } from "@/lib/api";

const EMPTY = { tracks: [], albums: [], artists: [], playlists: [], stations: [] };
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const SEARCH_HINT = isMac ? "⌘K" : "Ctrl K";

let openFn = null;
export const openSearch = () => openFn?.();

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const p = usePlayer();

  useEffect(() => {
    openFn = () => setOpen(true);
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); openFn = null; };
  }, []);

  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setRes(EMPTY); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => setRes(EMPTY)).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const go = (path, lib) => {
    if (lib === "owner" && getLibMode() !== "owner") setLibMode("owner");
    else if (!lib && getLibMode() === "owner") setLibMode("mine");
    setOpen(false);
    navigate(path);
  };
  const play = (t) => { p.playTracks([t]); setOpen(false); };
  const nothing = !loading && q.trim().length >= 2 && Object.values(res).every((l) => !l.length);
  const Lib = ({ lib }) => (lib === "owner" ? <span className="ml-auto rounded bg-sky-50 px-1.5 py-0.5 font-mono text-[9px] uppercase text-sky-700">Shared</span> : null);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl overflow-hidden p-0" data-testid="global-search" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Search</DialogTitle>
      <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.18em] [&_[cmdk-group-heading]]:text-zinc-400 [&_[cmdk-group]]:px-2 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2">
        <CommandInput value={q} onValueChange={setQ} placeholder="Search songs, albums, artists, playlists, radio…" data-testid="global-search-input" />
        <CommandList className="max-h-[60vh]">
          {q.trim().length < 2 && <p className="px-4 py-6 text-center text-xs text-zinc-400" data-testid="global-search-hint">Type at least two letters · Enter plays or opens · Esc closes</p>}
          {nothing && <CommandEmpty data-testid="global-search-empty">Nothing matches “{q}”.</CommandEmpty>}
          {res.tracks.length > 0 && (
            <CommandGroup heading="Songs">
              {res.tracks.map((t) => (
                <CommandItem key={t.id} value={`t-${t.id}`} onSelect={() => play(t)} data-testid={`search-track-${t.id}`} className="gap-3">
                  <Cover albumId={t.album_id} className="h-8 w-8 shrink-0" rounded="rounded" iconSize={12} />
                  <div className="min-w-0 flex-1"><p className="truncate text-sm">{t.title}</p><p className="truncate text-[11px] text-zinc-500">{t.artist} · {t.album}</p></div>
                  <Lib lib={t.lib} /><Play size={12} className="text-zinc-400" />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {res.albums.length > 0 && (
            <CommandGroup heading="Albums">
              {res.albums.map((a) => (
                <CommandItem key={a.id} value={`a-${a.id}`} onSelect={() => go(`/album/${a.id}`, a.lib)} data-testid={`search-album-${a.id}`} className="gap-3">
                  <Cover albumId={a.id} hasCover={a.has_cover} version={a.cover_version} className="h-8 w-8 shrink-0" rounded="rounded" iconSize={12} />
                  <div className="min-w-0 flex-1"><p className="truncate text-sm">{a.name}</p><p className="truncate text-[11px] text-zinc-500">{a.artist}{a.year ? ` · ${a.year}` : ""} · {a.song_count} songs</p></div>
                  <Lib lib={a.lib} /><Disc size={12} className="text-zinc-400" />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {res.artists.length > 0 && (
            <CommandGroup heading="Artists">
              {res.artists.map((a) => (
                <CommandItem key={a.name} value={`ar-${a.name}`} onSelect={() => go(`/artist?name=${encodeURIComponent(a.name)}`, a.lib)} data-testid={`search-artist-${a.name}`} className="gap-3">
                  <Users size={14} className="text-zinc-400" /><span className="flex-1 truncate text-sm">{a.name}</span><span className="font-mono text-[11px] text-zinc-400">{a.song_count} songs</span><Lib lib={a.lib} />
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {res.playlists.length > 0 && (
            <CommandGroup heading="Playlists">
              {res.playlists.map((pl) => (
                <CommandItem key={pl.id} value={`p-${pl.id}`} onSelect={() => go(`/playlists/${pl.id}`)} data-testid={`search-playlist-${pl.id}`} className="gap-3">
                  <ListMusic size={14} className="text-zinc-400" /><span className="flex-1 truncate text-sm">{pl.name}</span><span className="font-mono text-[11px] text-zinc-400">{pl.track_count} songs</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {res.stations.length > 0 && (
            <CommandGroup heading="Radio">
              {res.stations.map((s) => (
                <CommandItem key={s.id} value={`s-${s.id}`} onSelect={() => go("/radio")} data-testid={`search-station-${s.id}`} className="gap-3">
                  <Radio size={14} className="text-zinc-400" /><span className="flex-1 truncate text-sm">{s.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
      </DialogContent>
    </Dialog>
  );
};

export const SearchButton = () => (
  <button type="button" onClick={openSearch} data-testid="nav-search-button" title={`Search (${SEARCH_HINT})`}
    className="ml-auto flex h-8 items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 text-xs text-zinc-500 transition-colors hover:border-zinc-900 hover:text-zinc-900">
    <Search size={13} /><span className="hidden sm:inline">Search</span><kbd className="hidden rounded border border-zinc-200 bg-zinc-50 px-1 font-mono text-[10px] sm:inline">{SEARCH_HINT}</kbd>
  </button>
);

